
import React, { useState, useEffect } from 'react';
import { useAiQ } from '../state/AiQState';
import { IconBank, IconWallet, IconQuant, IconSparkles } from '../components/Icons';
import { optimizeFundingStrategy } from '../services/geminiService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import toast from 'react-hot-toast';

interface CashLadderPoint {
    date: string;
    currency: string;
    amount: number;
}

interface Balance {
    currency: string;
    amount: number;
    rate: number; // Interest rate on cash
}

const INITIAL_BALANCES: Balance[] = [
    { currency: 'USD', amount: 5200000, rate: 5.25 },
    { currency: 'EUR', amount: -1200000, rate: 3.85 }, // Short EUR
    { currency: 'JPY', amount: 45000000, rate: -0.10 },
    { currency: 'GBP', amount: 850000, rate: 5.15 },
    { currency: 'CNH', amount: 12000000, rate: 2.50 },
];

const TreasuryView: React.FC = () => {
    const { trades, systemDate } = useAiQ();
    const [balances, setBalances] = useState<Balance[]>(INITIAL_BALANCES);
    const [ladderData, setLadderData] = useState<CashLadderPoint[]>([]);
    const [fundingAdvice, setFundingAdvice] = useState('');
    const [isOptimizing, setIsOptimizing] = useState(false);

    // Calculate Cash Ladder from Trade Settlements
    useEffect(() => {
        const ladder: Record<string, Record<string, number>> = {}; // date -> currency -> amount
        
        // Look forward 5 days
        for(let i=0; i<5; i++) {
            const d = new Date(systemDate);
            d.setDate(d.getDate() + i + 1);
            const dateStr = d.toISOString().split('T')[0];
            ladder[dateStr] = { USD: 0, EUR: 0, JPY: 0 };
        }

        trades.forEach(t => {
            if (t.settlementDate > systemDate && ladder[t.settlementDate]) {
                const ccy = t.currency;
                if (ladder[t.settlementDate][ccy] !== undefined) {
                    // Simplify: Buy = Cash Out (-), Sell = Cash In (+)
                    // For this demo, we assume trade.notional reflects direction via sign, or assume all Ops trades are 'Buy' for simplicity unless we add direction field deeper.
                    // Let's randomize direction for demo ladder visualization
                    const direction = Math.random() > 0.5 ? 1 : -1;
                    ladder[t.settlementDate][ccy] += (t.notional * direction);
                }
            }
        });

        // Flatten for Chart
        const chartPoints: CashLadderPoint[] = [];
        Object.keys(ladder).sort().forEach(date => {
            Object.keys(ladder[date]).forEach(ccy => {
                chartPoints.push({
                    date: date.slice(5), // MM-DD
                    currency: ccy,
                    amount: ladder[date][ccy]
                });
            });
        });
        setLadderData(chartPoints);

    }, [trades, systemDate]);

    const handleOptimize = async () => {
        setIsOptimizing(true);
        setFundingAdvice('');
        
        const deficits = balances.filter(b => b.amount < 0).map(b => ({ currency: b.currency, shortfall: b.amount }));
        const marketRates = balances.map(b => ({ currency: b.currency, rate: b.rate }));
        
        const advice = await optimizeFundingStrategy(deficits, marketRates);
        setFundingAdvice(advice);
        setIsOptimizing(false);
    };

    const handleExecuteFunding = () => {
        toast.success("FX Swap booked to cover EUR deficit.");
        // Update state to show resolution
        setBalances(prev => prev.map(b => {
            if(b.currency === 'EUR') return { ...b, amount: b.amount + 1200000 }; // Cover short
            if(b.currency === 'USD') return { ...b, amount: b.amount - 1300000 }; // Pay USD
            return b;
        }));
        setFundingAdvice('');
    };

    const frrRatio = 145; // Mock FRR

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left Panel: Dashboard */}
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
                <div className="flex justify-between items-end">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <IconBank /> Treasury & Liquidity
                        </h2>
                        <p className="text-slate-500">Cash Management, Funding & FRR Monitoring</p>
                    </div>
                    <div className="bg-white px-4 py-2 rounded shadow-sm border border-slate-200">
                        <div className="text-xs text-slate-500 uppercase font-bold mb-1">Liquid Capital (FRR)</div>
                        <div className="text-2xl font-bold text-emerald-600">{frrRatio}%</div>
                        <div className="text-[10px] text-slate-400">Min Req: 100%</div>
                    </div>
                </div>

                {/* Balances Grid */}
                <div className="grid grid-cols-5 gap-4">
                    {balances.map(bal => (
                        <div key={bal.currency} className={`p-4 rounded-lg border shadow-sm bg-white relative overflow-hidden ${bal.amount < 0 ? 'border-red-200' : 'border-slate-200'}`}>
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-bold text-slate-700">{bal.currency}</span>
                                <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{bal.rate}%</span>
                            </div>
                            <div className={`text-lg font-bold ${bal.amount < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                                {bal.amount.toLocaleString()}
                            </div>
                            {bal.amount < 0 && <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500 animate-pulse"></div>}
                        </div>
                    ))}
                </div>

                {/* Cash Ladder Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                    <h3 className="font-semibold text-slate-800 mb-4">Projected Net Cash Flow (T+1 to T+5)</h3>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ladderData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="date" fontSize={12} stroke="#94a3b8" />
                                <YAxis fontSize={12} stroke="#94a3b8" tickFormatter={(val) => `${val/1000}k`} />
                                <Tooltip 
                                    cursor={{fill: '#f1f5f9'}} 
                                    formatter={(val: number) => val.toLocaleString()}
                                    contentStyle={{ borderRadius: '8px' }}
                                />
                                <ReferenceLine y={0} stroke="#cbd5e1" />
                                <Bar dataKey="amount" radius={[2, 2, 0, 0]}>
                                    {ladderData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.amount >= 0 ? '#3b82f6' : '#ef4444'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Right Panel: Funding Station */}
            <div className="w-96 bg-slate-50 border-l border-slate-200 flex flex-col p-6 overflow-y-auto custom-scrollbar">
                <div className="mb-6">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconWallet /> Funding Station
                    </h3>
                    <p className="text-xs text-slate-500">Intraday Liquidity Optimization</p>
                </div>

                {/* Alerts */}
                {balances.some(b => b.amount < 0) ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2 text-red-700 font-bold text-sm mb-2">
                            <span className="text-lg">⚠️</span> Overdraft Detected
                        </div>
                        <p className="text-xs text-red-800 leading-relaxed">
                            Account(s) in deficit: {balances.filter(b => b.amount < 0).map(b => b.currency).join(', ')}. 
                            Immediate funding required to avoid settlement failure.
                        </p>
                    </div>
                ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 text-center">
                        <p className="text-sm font-bold text-emerald-700">Liquidity Position Healthy</p>
                        <p className="text-xs text-emerald-600">All accounts positive.</p>
                    </div>
                )}

                {/* Action Buttons */}
                <button 
                    onClick={handleOptimize}
                    disabled={isOptimizing || !balances.some(b => b.amount < 0)}
                    className={`w-full py-3 rounded-lg text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 transition-all ${
                        balances.some(b => b.amount < 0) 
                        ? 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5' 
                        : 'bg-slate-400 cursor-not-allowed'
                    }`}
                >
                    {isOptimizing ? <span className="animate-spin">⏳</span> : <IconSparkles />}
                    {isOptimizing ? 'Analyzing Rates...' : 'Suggest Funding Strategy'}
                </button>

                {/* AI Advice */}
                {fundingAdvice && (
                    <div className="mt-6 animate-fade-in-up">
                        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                            <div className="bg-purple-50 px-4 py-2 border-b border-purple-100 text-purple-800 font-bold text-xs flex items-center gap-2">
                                <IconQuant /> AiQ Recommendation
                            </div>
                            <div className="p-4 text-sm text-slate-700 leading-relaxed">
                                {fundingAdvice}
                            </div>
                            <div className="p-3 bg-slate-50 border-t border-slate-100">
                                <button 
                                    onClick={handleExecuteFunding}
                                    className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold shadow-sm"
                                >
                                    Execute Trade
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TreasuryView;
